import React, { useEffect, useState } from "react";
import { IoDownloadOutline, IoEyeOutline, IoReceiptOutline, IoSearchOutline, IoCloseCircle } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { motion } from "framer-motion";
import axios from "axios";
import EmployeePayslipLoading from "../../utils/EmployeePayslipLoading";
import api from "../../utils/api";

const E_Payslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [userId, setUserId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const payslipsPerPage = 6;
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(true);

  // Ensure payslips is always an array
  const safePayslips = Array.isArray(payslips) ? payslips : [];

  // Filtered payslips based on search
  const filteredPayslips = safePayslips.filter((p) =>
    (p.salaryPeriod || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPayslips.length / payslipsPerPage);
  const paginatedPayslips = filteredPayslips.slice(
    (currentPage - 1) * payslipsPerPage,
    currentPage * payslipsPerPage
  );

  useEffect(() => {
    const fetchPayslips = async () => {
      try {
        setLoading(true);
        const authRes = await api.get(`/api/current-user`, {
          withCredentials: true,
        });
        const { id } = authRes.data;
        setUserId(id);

        const payslipRes = await api.get(`/userpayslip/${id}`);
        setPayslips(payslipRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setUserId(0);
        setPayslips([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPayslips();
  }, []);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleView = (payid) => navigate(`/employee/payslips/E_View/${payid}`);

  const handleDownload = async (payid, salaryPeriod, employeeId) => {
    try {
      const res = await api.get(`/payslip/download/${payid}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${employeeId}_payslip_${salaryPeriod}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF:", err);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="sm:p-4 pt-4 sm:p-6 mb-20 w-full mx-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 bg-slate-200 border border-slate-300 rounded-2xl p-6"
        transition={{ duration: 0.4, type: "spring" }}
      >
        <div className="flex items-center gap-4 text-xl xs:text-2xl font-bold truncate">
          <div className="p-2 rounded-xl bg-slate-700 text-slate-100">
            <IoReceiptOutline className="" />
          </div>
          <h2 className="">My Payslips</h2>
        </div>

        {/* Search Bar */}
        <div className="flex bg-slate-100 py-3 px-4 rounded-full items-center gap-2 shadow-md  w-full sm:w-64">
          <IoSearchOutline className="" />
          <input
            type="text"
            placeholder=""
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="focus:outline-none bg-slate-100"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className=""
            >
              <IoCloseCircle className="" />
            </button>
          )}
        </div>
      </motion.div>

      {/* Desktop Table */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        transition={{ duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100 text-left text-slate-600">
              <tr>
                <th className="px-6 py-4 font-semibold">S.No.</th>
                <th className="px-6 py-4 font-semibold">Salary period</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold">Paid Days</th>
                <th className="px-6 py-4 font-semibold">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-slate-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : paginatedPayslips.length > 0 ? (
                paginatedPayslips.map((p, index) => (
                  <tr
                    key={p.payid}
                    onClick={() => handleView(p.payid)}
                    className="hover:bg-slate-50/50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-slate-600">
                      {(currentPage - 1) * payslipsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4">{p.salaryPeriod}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                        Paid
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const [monthStr, yearStr] = p.salaryPeriod.split("-");
                        const month = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
                        const year = parseInt(yearStr);
                        const totalDays = new Date(year, month + 1, 0).getDate();
                        return `${p.paidDays}/${totalDays}`;
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(p.payid, p.salaryPeriod, p.employeeId);
                          }}
                          title="Download Payslip"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="p-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors duration-150 border border-slate-200"
                        >
                          <IoDownloadOutline className="text-lg" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                    {searchTerm
                      ? `No payslips found matching "${searchTerm}"`
                      : "No payslips found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-100">
            <Stack spacing={2} className="flex justify-end">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="rounded-full"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#475569",
                    borderColor: "#e2e8f0",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#475569 !important",
                    color: "#fff",
                  },
                }}
              />
            </Stack>
          </div>
        )}
      </motion.div>

      {/* Mobile Card */}
      <motion.div className="sm:hidden space-y-4" transition={{ duration: 0.5 }}>
        {loading ? (
          <EmployeePayslipLoading />
        ) : paginatedPayslips.length > 0 ? (
          paginatedPayslips.map((p) => (
            <motion.div
              onClick={() => handleView(p.payid)}
              key={p.payid}
              className="bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300"
              whileHover={{ scale: 1.01 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-slate-800">
                    {p.salaryPeriod}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                    Paid
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xl font-semibold text-slate-700">
                    ₹ {p.netPayable}
                  </span>
                  <div className="flex gap-3">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(p.payid, p.salaryPeriod, p.employeeId);
                      }}
                      className="p-2 text-slate-600 hover:bg-slate-50"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Download Payslip"
                    >
                      <IoDownloadOutline className="text-2xl" />
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="bg-white p-8 rounded-xl shadow-md text-center text-slate-500">
            {searchTerm
              ? `No payslips found matching "${searchTerm}"`
              : "No payslips found."}
          </div>
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="rounded-full"
                size="medium"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#475569",
                    borderColor: "#e2e8f0",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#475569 !important",
                    color: "#fff",
                  },
                }}
              />
            </Stack>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default E_Payslips;